import { createSelector } from "@reduxjs/toolkit";
import { openapi, type GetEarthingRunsApiResponse } from "../../../openapi";
import type { ICalculateEarthingInput } from "@multiconsult-group/multiplacer-core";
import type { IPixPath, IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import type { IRootState } from "../../app/store";
import { routingGraphToPixPaths } from "../../DesignTab/routing/routingGrahpUti";
import {
  selectProjectViewerSelectedEarthingApiRequestId,
  selectProjectViewerSelectedEarthingApiRequestLiveError,
  selectProjectViewerSelectedEarthingApiRequestLiveResult,
} from "../projectViewerSlice";

const API_ARG = { limit: 100 };
const EMPTY_RUNS: GetEarthingRunsApiResponse["items"] = [];
const EMPTY_PIX_PATHS: IPixPath[] = [];
const MAX_VIEWER_POLYGONS = 1000;
const MAX_VIEWER_PATHS = 5000;

const withPathIdPrefix = (paths: IPixPath[], prefix: string): IPixPath[] =>
  paths.map((path) => ({
    ...path,
    id: `${prefix}-${path.id}`,
  }));

const limitRoutingGraphEdges = (
  graph: NonNullable<GetEarthingRunsApiResponse["items"][number]["response"]["output"]>,
  maxEdges: number
) => (graph.edges.length > maxEdges ? { ...graph, edges: graph.edges.slice(0, maxEdges) } : graph);

export type IEarthingApiRequestRow = {
  id: string;
  createdAt: string;
  status: "success" | "error";
  durationMs: number | null;
  requestId: string;
  clientName: string;
  clientVersion: string;
  runtimeName: string;
  runtimeVersion: string;
  apparatusFoundationCount: number;
  nodeCount: number;
  edgeCount: number;
  errorMessage: string;
  raw: GetEarthingRunsApiResponse["items"][number];
};

export const earthingApiRequestsQueryArg = API_ARG;

export const selectEarthingApiRequestsQuery = (state: IRootState) =>
  openapi.endpoints.getEarthingRuns.select(API_ARG)(state);

export const selectEarthingApiRequestRuns = (state: IRootState) =>
  selectEarthingApiRequestsQuery(state).data?.items ?? EMPTY_RUNS;

export const selectEarthingApiRequestsHasMore = (state: IRootState) =>
  selectEarthingApiRequestsQuery(state).data?.hasMore ?? false;

export const selectEarthingApiRequestRows = createSelector(
  [selectEarthingApiRequestRuns],
  (runs): IEarthingApiRequestRow[] =>
    runs.map((run, index) => ({
      id: run._id ?? run.request.requestId ?? `${run.createdAt}-${index}`,
      createdAt: run.createdAt,
      status: run.status,
      durationMs: run.durationMs ?? null,
      requestId: run.request.requestId ?? "",
      clientName: run.request.client?.clientName ?? "",
      clientVersion: run.request.client?.clientVersion ?? "",
      runtimeName: run.request.client?.runtimeName ?? "",
      runtimeVersion: run.request.client?.runtimeVersion ?? "",
      apparatusFoundationCount:
        run.meta?.apparatusFoundationCount ?? run.request.input?.apparatusFoundations.length ?? 0,
      nodeCount: run.meta?.nodeCount ?? run.response.output?.nodes.length ?? 0,
      edgeCount: run.meta?.edgeCount ?? run.response.output?.edges.length ?? 0,
      errorMessage: run.response.error?.message ?? "",
      raw: run,
    }))
);

export const selectSelectedEarthingApiRequestRow = createSelector(
  [selectEarthingApiRequestRows, selectProjectViewerSelectedEarthingApiRequestId],
  (rows, selectedId): IEarthingApiRequestRow | null =>
    selectedId == null ? null : (rows.find((row) => row.id === selectedId) ?? null)
);

export const selectSelectedEarthingApiRequestInputPolygons = createSelector(
  [selectSelectedEarthingApiRequestRow],
  (selectedRow): IPixPolygon[] =>
    selectedRow?.raw.request.input?.apparatusFoundations
      .slice(0, MAX_VIEWER_POLYGONS)
      .map((foundation) => ({
        id: `earthing-api-input-${foundation.id}`,
        entityId: foundation.id,
        points: foundation.points.map((point): [number, number] => [
          point[0] ?? 0,
          point[1] ?? 0,
        ]),
        fillColor: "#c92a2a33",
        strokeColor: "#c92a2a",
        strokeWidth: "2",
        tooltip: `Apparatfundament\n${foundation.id}`,
        text: {
          values: [foundation.id],
          fontSize: "12",
          color: "#862e2e",
        },
        isNotClickable: false,
        isMoveable: false,
        isDragSelectable: false,
      })) ?? []
);

export const selectSelectedEarthingApiRequestOutputPaths = createSelector(
  [selectSelectedEarthingApiRequestRow],
  (selectedRow): IPixPath[] => {
    const output = selectedRow?.raw.response.output;
    if (!output) return [];

    return withPathIdPrefix(
      routingGraphToPixPaths(limitRoutingGraphEdges(output, MAX_VIEWER_PATHS), {
        strokeColor: "#9c36b5",
        strokeWidthMultiplier: 2,
      }),
      "stored"
    );
  }
);

export const selectSelectedEarthingApiRequestLiveOutputPaths = createSelector(
  [selectProjectViewerSelectedEarthingApiRequestLiveResult],
  (liveResult): IPixPath[] => {
    if (!liveResult) return EMPTY_PIX_PATHS;

    return withPathIdPrefix(
      routingGraphToPixPaths(limitRoutingGraphEdges(liveResult, MAX_VIEWER_PATHS), {
        strokeColor: "#0c8599",
        strokeWidthMultiplier: 2,
      }),
      "live"
    );
  }
);

export const selectSelectedEarthingApiRequestViewerLimitSummary = createSelector(
  [selectSelectedEarthingApiRequestRow, selectProjectViewerSelectedEarthingApiRequestLiveResult],
  (selectedRow, liveResult): string | null => {
    if (!selectedRow) return null;

    const inputCount = selectedRow.raw.request.input?.apparatusFoundations.length ?? 0;
    const outputCount = selectedRow.raw.response.output?.edges.length ?? 0;
    const liveOutputCount = liveResult?.edges.length ?? 0;
    const messages: string[] = [];

    if (inputCount > MAX_VIEWER_POLYGONS) {
      messages.push(`Viser ${MAX_VIEWER_POLYGONS}/${inputCount} fundamenter`);
    }

    if (outputCount > MAX_VIEWER_PATHS) {
      messages.push(`viser ${MAX_VIEWER_PATHS}/${outputCount} lagrede kanter`);
    }

    if (liveOutputCount > MAX_VIEWER_PATHS) {
      messages.push(`viser ${MAX_VIEWER_PATHS}/${liveOutputCount} live-kanter`);
    }

    return messages.length ? `${messages.join(", ")}.` : null;
  }
);

export const selectSelectedEarthingApiRequestViewerPaths = createSelector(
  [selectSelectedEarthingApiRequestOutputPaths, selectSelectedEarthingApiRequestLiveOutputPaths],
  (storedPaths, livePaths): IPixPath[] => [...storedPaths, ...livePaths]
);

export const selectSelectedEarthingApiRequestInput = createSelector(
  [selectSelectedEarthingApiRequestRow],
  (selectedRow): ICalculateEarthingInput | null => {
    const input = selectedRow?.raw.request.input;
    if (!input) return null;

    return {
      apparatusFoundations: input.apparatusFoundations.map((foundation) => ({
        id: foundation.id,
        points: foundation.points.map((point): [number, number] => [
          point[0] ?? 0,
          point[1] ?? 0,
        ]),
      })),
    };
  }
);

export const selectSelectedEarthingApiRequestLiveError =
  selectProjectViewerSelectedEarthingApiRequestLiveError;

export const selectSelectedEarthingApiRequestHasLiveResult = createSelector(
  [selectProjectViewerSelectedEarthingApiRequestLiveResult],
  (liveResult): boolean => liveResult != null
);

export const selectSelectedEarthingApiRequestLiveSummary = createSelector(
  [selectProjectViewerSelectedEarthingApiRequestLiveResult],
  (liveResult): string | null => {
    if (!liveResult) return null;

    return `Tegnet i cyan: ${liveResult.nodes.length} noder, ${liveResult.edges.length} kanter.`;
  }
);

export const selectSelectedEarthingApiRequestZoomExtentKey = createSelector(
  [
    selectSelectedEarthingApiRequestRow,
    selectProjectViewerSelectedEarthingApiRequestLiveResult,
  ],
  (selectedRow, liveResult): string | null => {
    if (!selectedRow) return null;

    const inputCount = selectedRow.raw.request.input?.apparatusFoundations.length ?? 0;
    const outputCount = selectedRow.raw.response.output?.edges.length ?? 0;
    const liveOutputCount = liveResult?.edges.length ?? 0;

    return `${selectedRow.id}:${inputCount}:${outputCount}:${liveOutputCount}`;
  }
);
